'use client';

import Conditional from '@next-app/components/Conditional';
import LLInput from '@next-app/components/elements/LLInput/LLInput';
import LLRadioAccordion from '@next-app/components/elements/LLRadioAccordion/LLRadioAccordion';
import LLSwitch from '@next-app/components/elements/LLSwitch/LLSwitch';
import {
  selectIsPaymentMethodStep,
  selectIsCheckoutDetailsLoaded,
  selectHasItemFromRegistry,
  selectHasItemFromShippingList,
  selectPaymentsInfo,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setGiftItemIsAnonymous,
  setGiftingItemFrom,
} from '@next-app/lib/features/checkout/CheckoutSlice';
import {
  CHECKOUT_VALIDATION_MESSAGES,
  FIELD_INVALID_CHARACTERS,
  FIELD_VALIDATION_RULES,
} from '@next-app/constants/CheckoutConstants';
import { selectUserProfile } from '@next-app/lib/features/InitSelectors';
import { sendOnBlurDTMEvent } from '@next-app/utils/CheckoutHelper';

const GiftingNameSection = () => {
  const dispatch = useDispatch();
  const isPaymentMethodStep = useSelector(selectIsPaymentMethodStep);
  const isCheckoutDetailsLoaded = useSelector(selectIsCheckoutDetailsLoaded);
  const isRegistryOrder = useSelector(selectHasItemFromRegistry);
  const isCTLOrder = useSelector(selectHasItemFromShippingList);
  const paymentInfo = useSelector(selectPaymentsInfo);
  const profile = useSelector(selectUserProfile);

  const { firstName = '', lastName = '' } = profile || {};

  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  const [makeGiftAnonymous, setMakeGiftAnonymous] = useState(false);
  const [giftItemFrom, setGiftItemFrom] = useState('');
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    if (isRegistryOrder || isCTLOrder) {
      setGiftItemFrom(fullName);
      dispatch(setGiftingItemFrom(fullName));
    }
  }, [fullName, isRegistryOrder, isCTLOrder]);

  const switchGiftingName = (e: React.MouseEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).checked;
    dispatch(setGiftItemIsAnonymous(value));
    if (value) {
      setFieldError('');
      dispatch(setGiftingItemFrom(''));
    } else {
      if (giftItemFrom && !FIELD_VALIDATION_RULES.FIRST_NAME.test(giftItemFrom))
        setFieldError(CHECKOUT_VALIDATION_MESSAGES.GIFT_FROM_INVALID);
      dispatch(setGiftingItemFrom(giftItemFrom));
    }
    setMakeGiftAnonymous((prev) => !prev);
  };

  const onBlurHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value) setFieldError(CHECKOUT_VALIDATION_MESSAGES.GIFT_FROM_REQUIRED);
    else if (!FIELD_VALIDATION_RULES.FIRST_NAME.test(value))
      setFieldError(CHECKOUT_VALIDATION_MESSAGES.GIFT_FROM_INVALID);
    sendOnBlurDTMEvent({
      key: 'GiftItemFrom',
      errorMessage: fieldError,
      value,
    });
  };

  return (
    <Conditional
      if={
        isCheckoutDetailsLoaded &&
        isPaymentMethodStep &&
        (isRegistryOrder || isCTLOrder)
      }
    >
      <div className="my-ll-5">
        <LLRadioAccordion
          isEditable={true}
          isExpanded={true}
          showRadioButton={false}
        >
          <span>How should your name appear to the recipient?</span>
        </LLRadioAccordion>
        <div className="p-ll-3 border rounded-bottom bg-light">
          <LLInput
            // customClass={'mb-4'}
            placeholder="Name"
            value={giftItemFrom}
            isDisabled={makeGiftAnonymous}
            errorMsg={fieldError}
            maxLength={80}
            onChange={(e) => {
              setFieldError('');
              let value = (e.target as HTMLInputElement).value;
              ((value = value.replace(
                FIELD_INVALID_CHARACTERS.REGISTRY_GIFT_NAME,
                '',
              )),
                setGiftItemFrom(value));
              dispatch(setGiftingItemFrom(value));
            }}
            onBlur={onBlurHandler}
          ></LLInput>
          <div className="mt-ll-3">
            <LLSwitch checked={makeGiftAnonymous} onClick={switchGiftingName}>
              {'Make my gift anonymous.'}
            </LLSwitch>
          </div>
        </div>
      </div>
    </Conditional>
  );
};

export default GiftingNameSection;
